import React, { useState, useEffect, useCallback } from "react";
import { Card, Button, Tag, Space, Typography, theme, Spin, Alert, Tooltip, Statistic, Row, Col, Divider } from "antd";
import {
  ReloadOutlined,
  CheckCircleOutlined,
  DatabaseOutlined,
  WarningOutlined,
  ExclamationCircleOutlined,
  HddOutlined,
  ApiOutlined,
  SaveOutlined,
  ClusterOutlined,
  SyncOutlined,
  DashboardOutlined,
} from "@ant-design/icons";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  CartesianGrid,
} from "recharts";
import PageLayout from "../pageLayout/PageLayout.react";

const { Text, Title } = Typography;

const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");

function formatUptime(seconds) {
  if (!seconds) return "—";
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m} minutes`;
}

function formatBytes(bytes) {
  if (!bytes || bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

function formatTimestamp(unixSeconds) {
  if (!unixSeconds || unixSeconds === 0) return "Never";
  const date = new Date(unixSeconds * 1000);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

function formatChartTime(isoTime) {
  if (!isoTime) return "";
  const date = new Date(isoTime);
  return date.toLocaleTimeString("en-AU", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

export default function RedisStatus() {
  const { token } = theme.useToken();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [redisInfo, setRedisInfo] = useState(null);
  const [metricsHistory, setMetricsHistory] = useState([]);
  const [serverTimezone, setServerTimezone] = useState(null);

  const fetchRedisStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE}/api/queues/status`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      if (!data.redis) {
        setError(data.message || "Redis is not reachable");
        setRedisInfo(null);
      } else {
        setRedisInfo(data.redisInfo);
      }
    } catch (err) {
      setError(err.message);
      setRedisInfo(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchMetricsHistory = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/queues/redis-history`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      setMetricsHistory(data.history || []);
    } catch (err) {
      console.error("Failed to fetch metrics history:", err);
    }
  }, []);

  const fetchServerTimezone = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/server-info`);
      if (response.ok) {
        const data = await response.json();
        setServerTimezone(data.timezone || "Australia/Brisbane");
      }
    } catch (err) {
      console.error("Failed to fetch server timezone:", err);
    }
  }, []);

  useEffect(() => {
    fetchRedisStatus();
    fetchMetricsHistory();
    fetchServerTimezone();

    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchRedisStatus();
      fetchMetricsHistory();
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchRedisStatus, fetchMetricsHistory, fetchServerTimezone]);

  const handleRefresh = () => {
    fetchRedisStatus();
    fetchMetricsHistory();
  };

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label, valueFormatter, valueSuffix }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          background: token.colorBgElevated,
          border: `1px solid ${token.colorBorderSecondary}`,
          borderRadius: 4,
          padding: "8px 12px",
          boxShadow: token.boxShadowSecondary,
        }}>
          <Text style={{ display: "block", marginBottom: 4 }}>{label}</Text>
          <Text strong style={{ color: payload[0].color }}>
            {valueFormatter ? valueFormatter(payload[0].value) : payload[0].value}{valueSuffix || ""}
          </Text>
        </div>
      );
    }
    return null;
  };

  const ri = redisInfo;

  if (loading && !ri) {
    return (
      <PageLayout active="redisStatus">
        <div style={{ textAlign: "center", padding: 60 }}>
          <Spin size="large" tip="Loading Redis status..." />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout active="redisStatus">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>Redis Monitoring</Title>
          <Text type="secondary">Production-grade Redis metrics and health monitoring</Text>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={handleRefresh} loading={loading}>
            Refresh
          </Button>
        </Space>
      </div>

      {error && (
        <Alert message="Error" description={error} type="error" showIcon style={{ marginBottom: 16 }} />
      )}

      {ri && (
        <>
          {/* Header with status tags */}
          <Card size="small" style={{ marginBottom: 16 }}>
            <Space size="middle" wrap>
              <Space>
                <DatabaseOutlined style={{ color: "#cf1322", fontSize: 20 }} />
                <Text strong style={{ fontSize: 16 }}>Redis</Text>
              </Space>
              <Tag color={ri.loading ? "warning" : "success"}>{ri.loading ? "Loading" : "Online"}</Tag>
              <Tag>{ri.role === "master" ? "Master" : "Replica"}</Tag>
              <Tag>v{ri.redisVersion}</Tag>
              <Tag>{ri.mode}</Tag>
              {ri.clusterEnabled && <Tag color="purple">Cluster</Tag>}
              <Text type="secondary">Uptime: {formatUptime(ri.uptimeSeconds)}</Text>
            </Space>
          </Card>

          {/* Charts Row */}
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            {/* Ops/sec Chart */}
            <Col xs={24} lg={12}>
              <Card
                size="small"
                title={
                  <Space>
                    <DashboardOutlined style={{ color: "#722ed1" }} />
                    <span>Operations Per Second</span>
                    {metricsHistory.length > 0 && (
                      <Tag>{metricsHistory.length} points</Tag>
                    )}
                  </Space>
                }
              >
                <div style={{ height: 250 }}>
                  {metricsHistory.length > 1 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={metricsHistory} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="opsGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#722ed1" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#722ed1" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} />
                        <XAxis
                          dataKey="timestamp"
                          tickFormatter={formatChartTime}
                          stroke={token.colorTextSecondary}
                          fontSize={11}
                          interval="preserveStartEnd"
                        />
                        <YAxis
                          stroke={token.colorTextSecondary}
                          fontSize={11}
                          tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(1)}K` : v}
                        />
                        <RechartsTooltip
                          content={<CustomTooltip valueSuffix=" ops/sec" />}
                          labelFormatter={formatChartTime}
                        />
                        <Area
                          type="monotone"
                          dataKey="opsPerSec"
                          stroke="#722ed1"
                          strokeWidth={2}
                          fill="url(#opsGradient)"
                          dot={false}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: token.colorTextSecondary }}>
                      <Text type="secondary">Collecting data... (refreshes every minute)</Text>
                    </div>
                  )}
                </div>
                <div style={{ textAlign: "center", marginTop: 8 }}>
                  <Statistic
                    title="Current"
                    value={ri.instantaneousOpsPerSec || 0}
                    suffix="ops/sec"
                    valueStyle={{ fontSize: 18, color: "#722ed1" }}
                  />
                </div>
              </Card>
            </Col>

            {/* Memory Chart */}
            <Col xs={24} lg={12}>
              <Card
                size="small"
                title={
                  <Space>
                    <HddOutlined style={{ color: ri.evictedKeys > 0 ? "#ff4d4f" : "#1890ff" }} />
                    <span>Memory Usage</span>
                    {ri.maxMemoryHuman && <Tag>Max: {ri.maxMemoryHuman}</Tag>}
                  </Space>
                }
              >
                <div style={{ height: 250 }}>
                  {metricsHistory.length > 1 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={metricsHistory} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                        <defs>
                          <linearGradient id="memGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#1890ff" stopOpacity={0.3} />
                            <stop offset="95%" stopColor="#1890ff" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke={token.colorBorderSecondary} />
                        <XAxis
                          dataKey="timestamp"
                          tickFormatter={formatChartTime}
                          stroke={token.colorTextSecondary}
                          fontSize={11}
                          interval="preserveStartEnd"
                        />
                        <YAxis
                          stroke={token.colorTextSecondary}
                          fontSize={11}
                          tickFormatter={(v) => `${v.toFixed(0)} MB`}
                        />
                        <RechartsTooltip
                          content={<CustomTooltip valueFormatter={(v) => v?.toFixed(2)} valueSuffix=" MB" />}
                          labelFormatter={formatChartTime}
                        />
                        <Area
                          type="monotone"
                          dataKey="usedMemoryMB"
                          stroke="#1890ff"
                          strokeWidth={2}
                          fill="url(#memGradient)"
                          dot={false}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: token.colorTextSecondary }}>
                      <Text type="secondary">Collecting data... (refreshes every minute)</Text>
                    </div>
                  )}
                </div>
                <div style={{ textAlign: "center", marginTop: 8 }}>
                  <Statistic
                    title="Current"
                    value={ri.usedMemoryHuman || "—"}
                    valueStyle={{
                      fontSize: 18,
                      color: parseFloat(ri.memoryUsagePct) > 80 ? "#ff4d4f" : "#1890ff",
                    }}
                    suffix={ri.maxMemoryHuman ? <Text type="secondary" style={{ fontSize: 12 }}>/ {ri.maxMemoryHuman}</Text> : null}
                  />
                </div>
              </Card>
            </Col>
          </Row>

          {/* Detailed Metrics - 6 Categories */}
          <Card size="small" title="Detailed Metrics">
            {/* Category 1: Basic Health */}
            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ display: "block", marginBottom: 8 }}>
                <CheckCircleOutlined style={{ color: "#52c41a", marginRight: 6 }} />
                Basic Health
              </Text>
              <Row gutter={[12, 8]}>
                <Col xs={8} sm={6} md={4} lg={3}>
                  <Statistic title="Version" value={ri.redisVersion || "—"} valueStyle={{ fontSize: 14 }} />
                </Col>
                <Col xs={8} sm={6} md={4} lg={3}>
                  <Statistic title="Uptime" value={formatUptime(ri.uptimeSeconds)} valueStyle={{ fontSize: 14 }} />
                </Col>
                <Col xs={8} sm={6} md={4} lg={3}>
                  <Statistic title="Mode" value={ri.mode || "standalone"} valueStyle={{ fontSize: 14 }} />
                </Col>
                <Col xs={8} sm={6} md={4} lg={3}>
                  <Tooltip title={`Max: ${ri.maxClients || 10000}`}>
                    <Statistic
                      title="Clients"
                      value={ri.connectedClients || 0}
                      valueStyle={{
                        fontSize: 14,
                        color: ri.connectedClients >= ri.maxClients * 0.8 ? "#ff4d4f" : undefined,
                      }}
                      suffix={ri.blockedClients > 0 ? <Text type="warning" style={{ fontSize: 10 }}>({ri.blockedClients} blocked)</Text> : null}
                    />
                  </Tooltip>
                </Col>
                <Col xs={8} sm={6} md={4} lg={3}>
                  <Statistic title="Keys" value={ri.totalKeys || 0} valueStyle={{ fontSize: 14 }} />
                </Col>
              </Row>
            </div>

            <Divider style={{ margin: "12px 0" }} />

            {/* Category 2: Memory (Critical) */}
            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ display: "block", marginBottom: 8 }}>
                <HddOutlined style={{ color: ri.evictedKeys > 0 || parseFloat(ri.memoryUsagePct) > 80 ? "#ff4d4f" : "#1890ff", marginRight: 6 }} />
                Memory
                {ri.evictedKeys > 0 && (
                  <Tag color="error" style={{ marginLeft: 8 }}>
                    <ExclamationCircleOutlined /> Evicting Keys!
                  </Tag>
                )}
                {parseFloat(ri.memoryUsagePct) > 80 && (
                  <Tag color="warning" style={{ marginLeft: 8 }}>
                    <WarningOutlined /> High Usage
                  </Tag>
                )}
              </Text>
              <Row gutter={[12, 8]}>
                <Col xs={8} sm={6} md={4} lg={3}>
                  <Tooltip title={`RSS: ${ri.usedMemoryRssHuman || "—"}`}>
                    <Statistic
                      title="Used"
                      value={ri.usedMemoryHuman || "—"}
                      valueStyle={{ fontSize: 14 }}
                      suffix={ri.maxMemoryHuman ? <Text type="secondary" style={{ fontSize: 10 }}>/ {ri.maxMemoryHuman}</Text> : null}
                    />
                  </Tooltip>
                </Col>
                <Col xs={8} sm={6} md={4} lg={3}>
                  <Statistic
                    title="Usage %"
                    value={ri.memoryUsagePct != null ? `${ri.memoryUsagePct}%` : "No limit"}
                    valueStyle={{
                      fontSize: 14,
                      color: parseFloat(ri.memoryUsagePct) > 90 ? "#ff4d4f" : parseFloat(ri.memoryUsagePct) > 80 ? "#faad14" : undefined,
                    }}
                  />
                </Col>
                <Col xs={8} sm={6} md={4} lg={3}>
                  <Statistic title="Peak" value={ri.usedMemoryPeakHuman || "—"} valueStyle={{ fontSize: 14 }} />
                </Col>
                <Col xs={8} sm={6} md={4} lg={3}>
                  <Tooltip title="Ratio > 1.5 indicates fragmentation. Consider restart.">
                    <Statistic
                      title="Fragmentation"
                      value={ri.fragmentationRatio?.toFixed(2) || "—"}
                      valueStyle={{
                        fontSize: 14,
                        color: ri.fragmentationRatio > 1.5 ? "#ff4d4f" : ri.fragmentationRatio > 1.2 ? "#faad14" : "#52c41a",
                      }}
                    />
                  </Tooltip>
                </Col>
                <Col xs={8} sm={6} md={4} lg={3}>
                  <Tooltip title="Keys evicted due to maxmemory. Non-zero = data loss risk in BullMQ!">
                    <Statistic
                      title="Evicted"
                      value={ri.evictedKeys || 0}
                      valueStyle={{
                        fontSize: 14,
                        color: ri.evictedKeys > 0 ? "#ff4d4f" : "#52c41a",
                      }}
                      prefix={ri.evictedKeys > 0 ? <ExclamationCircleOutlined /> : null}
                    />
                  </Tooltip>
                </Col>
                <Col xs={8} sm={6} md={4} lg={3}>
                  <Statistic title="Expired" value={ri.expiredKeys || 0} valueStyle={{ fontSize: 14 }} />
                </Col>
              </Row>
            </div>

            <Divider style={{ margin: "12px 0" }} />

            {/* Category 3: Performance & Latency */}
            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ display: "block", marginBottom: 8 }}>
                <DashboardOutlined style={{ color: "#722ed1", marginRight: 6 }} />
                Performance
                {ri.slowlogLen > 10 && (
                  <Tag color="warning" style={{ marginLeft: 8 }}>
                    <WarningOutlined /> {ri.slowlogLen} slow queries
                  </Tag>
                )}
              </Text>
              <Row gutter={[12, 8]}>
                <Col xs={8} sm={6} md={4} lg={3}>
                  <Statistic title="Ops/sec" value={ri.instantaneousOpsPerSec || 0} valueStyle={{ fontSize: 14 }} />
                </Col>
                <Col xs={8} sm={6} md={4} lg={3}>
                  <Statistic
                    title="Commands"
                    value={ri.totalCommandsProcessed ? (ri.totalCommandsProcessed > 1000000 ? `${(ri.totalCommandsProcessed / 1000000).toFixed(1)}M` : ri.totalCommandsProcessed > 1000 ? `${(ri.totalCommandsProcessed / 1000).toFixed(1)}K` : ri.totalCommandsProcessed) : 0}
                    valueStyle={{ fontSize: 14 }}
                  />
                </Col>
                <Col xs={8} sm={6} md={4} lg={3}>
                  <Tooltip title={`Hits: ${ri.keyspaceHits || 0}, Misses: ${ri.keyspaceMisses || 0}`}>
                    <Statistic
                      title="Hit Rate"
                      value={ri.hitRate != null ? `${ri.hitRate}%` : "—"}
                      valueStyle={{
                        fontSize: 14,
                        color: parseFloat(ri.hitRate) >= 90 ? "#52c41a" : parseFloat(ri.hitRate) >= 70 ? "#faad14" : "#ff4d4f",
                      }}
                    />
                  </Tooltip>
                </Col>
                <Col xs={8} sm={6} md={4} lg={3}>
                  <Tooltip title="Number of slow queries in SLOWLOG. Check with SLOWLOG GET.">
                    <Statistic
                      title="Slow Queries"
                      value={ri.slowlogLen || 0}
                      valueStyle={{
                        fontSize: 14,
                        color: ri.slowlogLen > 50 ? "#ff4d4f" : ri.slowlogLen > 10 ? "#faad14" : undefined,
                      }}
                    />
                  </Tooltip>
                </Col>
                <Col xs={8} sm={6} md={4} lg={3}>
                  <Tooltip title={`Sys: ${ri.cpuSys?.toFixed(2)}s, User: ${ri.cpuUser?.toFixed(2)}s`}>
                    <Statistic
                      title="CPU Time"
                      value={ri.cpuTotal != null ? `${ri.cpuTotal.toFixed(1)}s` : "—"}
                      valueStyle={{ fontSize: 14 }}
                    />
                  </Tooltip>
                </Col>
              </Row>
            </div>

            <Divider style={{ margin: "12px 0" }} />

            {/* Category 4: Persistence (RDB/AOF) */}
            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ display: "block", marginBottom: 8 }}>
                <SaveOutlined style={{ color: ri.rdbLastBgsaveStatus !== "ok" || ri.aofLastWriteStatus !== "ok" ? "#ff4d4f" : "#52c41a", marginRight: 6 }} />
                Persistence
                {ri.rdbBgsaveInProgress && <Tag color="processing" style={{ marginLeft: 8 }}><SyncOutlined spin /> RDB Saving</Tag>}
                {ri.aofRewriteInProgress && <Tag color="processing" style={{ marginLeft: 8 }}><SyncOutlined spin /> AOF Rewriting</Tag>}
                {ri.rdbLastBgsaveStatus !== "ok" && <Tag color="error" style={{ marginLeft: 8 }}>RDB Error</Tag>}
                {ri.aofEnabled && ri.aofLastWriteStatus !== "ok" && <Tag color="error" style={{ marginLeft: 8 }}>AOF Error</Tag>}
              </Text>
              <Row gutter={[12, 8]}>
                <Col xs={8} sm={6} md={4} lg={3}>
                  <Statistic
                    title="RDB Status"
                    value={ri.rdbLastBgsaveStatus || "ok"}
                    valueStyle={{ fontSize: 14, color: ri.rdbLastBgsaveStatus === "ok" ? "#52c41a" : "#ff4d4f" }}
                  />
                </Col>
                <Col xs={8} sm={6} md={4} lg={3}>
                  <Tooltip title={ri.rdbLastSaveTime ? new Date(ri.rdbLastSaveTime * 1000).toLocaleString() : "Never"}>
                    <Statistic
                      title="Last RDB Save"
                      value={formatTimestamp(ri.rdbLastSaveTime)}
                      valueStyle={{ fontSize: 14 }}
                    />
                  </Tooltip>
                </Col>
                <Col xs={8} sm={6} md={4} lg={3}>
                  <Statistic
                    title="Changes Since Save"
                    value={ri.rdbChangesSinceSave || 0}
                    valueStyle={{ fontSize: 14, color: ri.rdbChangesSinceSave > 10000 ? "#faad14" : undefined }}
                  />
                </Col>
                <Col xs={8} sm={6} md={4} lg={3}>
                  <Statistic
                    title="AOF"
                    value={ri.aofEnabled ? "Enabled" : "Disabled"}
                    valueStyle={{ fontSize: 14, color: ri.aofEnabled ? "#52c41a" : "#d9d9d9" }}
                  />
                </Col>
                {ri.aofEnabled && (
                  <>
                    <Col xs={8} sm={6} md={4} lg={3}>
                      <Statistic
                        title="AOF Write"
                        value={ri.aofLastWriteStatus || "ok"}
                        valueStyle={{ fontSize: 14, color: ri.aofLastWriteStatus === "ok" ? "#52c41a" : "#ff4d4f" }}
                      />
                    </Col>
                    <Col xs={8} sm={6} md={4} lg={3}>
                      <Statistic
                        title="AOF Size"
                        value={formatBytes(ri.aofCurrentSize)}
                        valueStyle={{ fontSize: 14 }}
                      />
                    </Col>
                  </>
                )}
              </Row>
            </div>

            <Divider style={{ margin: "12px 0" }} />

            {/* Category 5: Replication */}
            <div style={{ marginBottom: 16 }}>
              <Text strong style={{ display: "block", marginBottom: 8 }}>
                <ClusterOutlined style={{ color: ri.masterLinkStatus === "down" ? "#ff4d4f" : "#1890ff", marginRight: 6 }} />
                Replication
                {ri.role === "slave" && ri.masterLinkStatus === "down" && (
                  <Tag color="error" style={{ marginLeft: 8 }}>
                    <ExclamationCircleOutlined /> Master Link Down!
                  </Tag>
                )}
              </Text>
              <Row gutter={[12, 8]}>
                <Col xs={8} sm={6} md={4} lg={3}>
                  <Statistic
                    title="Role"
                    value={ri.role === "master" ? "Master" : "Replica"}
                    valueStyle={{ fontSize: 14 }}
                  />
                </Col>
                {ri.role === "master" && (
                  <Col xs={8} sm={6} md={4} lg={3}>
                    <Statistic title="Connected Replicas" value={ri.connectedSlaves || 0} valueStyle={{ fontSize: 14 }} />
                  </Col>
                )}
                {ri.role === "slave" && (
                  <>
                    <Col xs={8} sm={6} md={4} lg={3}>
                      <Statistic
                        title="Master Link"
                        value={ri.masterLinkStatus || "—"}
                        valueStyle={{ fontSize: 14, color: ri.masterLinkStatus === "up" ? "#52c41a" : "#ff4d4f" }}
                      />
                    </Col>
                    <Col xs={8} sm={6} md={4} lg={3}>
                      <Tooltip title={ri.masterHost ? `${ri.masterHost}:${ri.masterPort}` : "—"}>
                        <Statistic title="Master" value={ri.masterHost || "—"} valueStyle={{ fontSize: 14 }} />
                      </Tooltip>
                    </Col>
                    <Col xs={8} sm={6} md={4} lg={3}>
                      <Statistic
                        title="Last IO"
                        value={ri.masterLastIoSecondsAgo >= 0 ? `${ri.masterLastIoSecondsAgo}s ago` : "—"}
                        valueStyle={{
                          fontSize: 14,
                          color: ri.masterLastIoSecondsAgo > 10 ? "#ff4d4f" : ri.masterLastIoSecondsAgo > 5 ? "#faad14" : undefined,
                        }}
                      />
                    </Col>
                  </>
                )}
                {ri.replBacklogActive && (
                  <Col xs={8} sm={6} md={4} lg={3}>
                    <Statistic title="Backlog" value={formatBytes(ri.replBacklogSize)} valueStyle={{ fontSize: 14 }} />
                  </Col>
                )}
              </Row>
            </div>

            <Divider style={{ margin: "12px 0" }} />

            {/* Category 6: Network & Connections */}
            <div>
              <Text strong style={{ display: "block", marginBottom: 8 }}>
                <ApiOutlined style={{ color: ri.rejectedConnections > 0 ? "#ff4d4f" : "#52c41a", marginRight: 6 }} />
                Network
                {ri.rejectedConnections > 0 && (
                  <Tag color="error" style={{ marginLeft: 8 }}>
                    <WarningOutlined /> {ri.rejectedConnections} rejected
                  </Tag>
                )}
              </Text>
              <Row gutter={[12, 8]}>
                <Col xs={8} sm={6} md={4} lg={3}>
                  <Statistic title="Total Connections" value={ri.totalConnectionsReceived || 0} valueStyle={{ fontSize: 14 }} />
                </Col>
                <Col xs={8} sm={6} md={4} lg={3}>
                  <Statistic
                    title="Rejected"
                    value={ri.rejectedConnections || 0}
                    valueStyle={{ fontSize: 14, color: ri.rejectedConnections > 0 ? "#ff4d4f" : "#52c41a" }}
                  />
                </Col>
                <Col xs={8} sm={6} md={4} lg={3}>
                  <Statistic title="Net In" value={formatBytes(ri.totalNetInputBytes)} valueStyle={{ fontSize: 14 }} />
                </Col>
                <Col xs={8} sm={6} md={4} lg={3}>
                  <Statistic title="Net Out" value={formatBytes(ri.totalNetOutputBytes)} valueStyle={{ fontSize: 14 }} />
                </Col>
              </Row>
            </div>
          </Card>
        </>
      )}
    </PageLayout>
  );
}
